import React from "react";

import type { FileMetaDto } from "../../api";
import { Modal, useManualModalState } from "@synthesis/shared-ui";
import { cn } from "@synthesis/shared-utils";

import { MetaData, Versions, VersionsIFC } from "./local-components";

export const LayoutBlocks = {
  MetaData: "MetaData",
  Versions: "Versions",
} as const;

type LayoutBlock = (typeof LayoutBlocks)[keyof typeof LayoutBlocks];

interface AddModalMetaProps {
  info: FileMetaDto;
  selectedFile: FileMetaDto;
  onUpdateVersion: (metaId: string) => void;
  onClickModel: (data: FileMetaDto) => void;
  stateFunctions?: { openViewer: (file?: FileMetaDto) => void };
}

interface ModalMetaProps extends AddModalMetaProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  openModal: () => void;
  closeModal: () => void;
  stateFunctions?: { openViewer: (file?: FileMetaDto) => void };
}

const ModalMeta = React.memo((props: ModalMetaProps) => {
  const { info, selectedFile, onUpdateVersion, onClickModel, stateFunctions } = props;

  return (
    <Modal.Root manual {...props}>
      <AddModalMeta
        info={info}
        selectedFile={selectedFile}
        onUpdateVersion={onUpdateVersion}
        onClickModel={onClickModel}
        stateFunctions={stateFunctions}
      />
    </Modal.Root>
  );
});

const defaultStateFunctions = {
  openViewer: (_file?: FileMetaDto) => undefined,
};

const AddModalMeta = React.memo(({ info, selectedFile, onUpdateVersion, onClickModel, stateFunctions }: AddModalMetaProps) => {
  const { closeModal } = useManualModalState();
  const viewerStateFunctions = stateFunctions ?? defaultStateFunctions;

  const [layout, setLayout] = React.useState<LayoutBlock>(LayoutBlocks.MetaData);

  const onSelectLayout = (nextLayout: LayoutBlock) => {
    setLayout(nextLayout);
  };

  const handleCloseModalMeta = () => {
    closeModal();
  };

  const Menu: Record<LayoutBlock, React.ReactNode> = {
    [LayoutBlocks.MetaData]: <MetaData info={info} />,
    [LayoutBlocks.Versions]:
      info.type !== "MODEL" ? (
        <Versions selectedId={info.id} />
      ) : (
        <VersionsIFC
          onUpdateVersion={onUpdateVersion}
          info={info}
          selectedFile={selectedFile}
          selectedId={info.id}
          onClickModel={onClickModel}
          handleCloseModalMeta={handleCloseModalMeta}
          stateFunctions={viewerStateFunctions}
        />
      ),
  };

  return (
    <Modal.Content className="max-h-full pb-5">
      <Modal.Title data-cy="modalMeta-modalTitle">Информация</Modal.Title>
      <div className="flex bg-gray-200">
        <div className={cn("mt-1 w-32", layout === LayoutBlocks.MetaData && "border-b-4 border-gray-500")}>
          <button
            type="button"
            className="flex w-full items-center justify-center"
            data-cy="modalMeta-buttonChoice"
            onClick={() => onSelectLayout(LayoutBlocks.MetaData)}
          >
            Метаданные
          </button>
        </div>

        <div className={cn("mt-1 w-32", layout === LayoutBlocks.Versions && "border-b-4 border-gray-500")}>
          <button
            type="button"
            className="flex w-full items-center justify-center"
            data-cy="modalMeta-buttonChoice"
            onClick={() => onSelectLayout(LayoutBlocks.Versions)}
          >
            Версии
          </button>
        </div>
      </div>
      {Menu[layout]}
    </Modal.Content>
  );
});

export default ModalMeta;
